import { useEffect, useRef, useState } from 'react';

/**
 * USB SKANER — bu klaviatura.
 *
 * Ombordagi skaner alohida qurilma sifatida ko'rinmaydi: u kodni oddiy
 * tugma bosishlari bo'lib "yozadi" va oxirida `Enter` yuboradi. Ya'ni
 * brauzer uchun skaner bilan odam orasida hech qanday farq yo'q — farqni
 * faqat TEZLIK ko'rsatadi: skaner belgilarni bir tekis, o'nlab millisekund
 * oralig'ida yuboradi, odam esa hech qachon bunchalik tez yoza olmaydi.
 *
 * Nega bu farq kerak: ekran skanerlangan kodni DARHOL qabul qilishi kerak
 * (skladchining qo'li band, u tugma bosmaydi), lekin odam qo'lda yozayotgan
 * matnni har `Enter` da "skanerlandi" deb yuborib bo'lmaydi — u hali
 * tugatmagan bo'lishi mumkin.
 */

/** Bitta tugma bosilishi: qaysi tugma va qachon (ms). */
export interface Keystroke {
  key: string;
  at: number;
}

/**
 * Xulosa:
 *   - `scanner` — skaner yubordi, kod tayyor;
 *   - `human`   — odam yozdi, e'tiborsiz qoldiriladi (u o'zi tasdiqlaydi);
 *   - `pending` — hali tugamagan (`Enter` kelmagan).
 */
export type ScanVerdict =
  | { kind: 'scanner'; code: string }
  | { kind: 'human' }
  | { kind: 'pending' };

/**
 * Belgilar orasidagi eng katta tanaffus.
 *
 * 40 ms — o'lchangan chegara: eng arzon USB skanerlar ham belgilarni 5–20 ms
 * oralig'ida yuboradi, eng tez mashinkachi esa daqiqada ~700 belgi (≈85 ms)
 * dan tez yoza olmaydi. Oradagi bo'shliq keng va u ataylab shunday: chegarani
 * qisqartirish sekinroq skanerni "odam" qilib qo'yardi, uzaytirish esa
 * odamning tez yozgan qismini "skaner" deb yuborardi.
 */
export const MAX_SCANNER_GAP_MS = 40;

/** Belgi tugmasi — uzunligi bitta bo'lganlari (`Shift`, `Tab` emas). */
const isCharacter = (key: string): boolean => key.length === 1;

/**
 * Tugma bosishlar ketma-ketligini baholaydi.
 *
 * Sof funksiya: DOM ni ham, vaqtni ham bilmaydi va shuning uchun chegara
 * brauzersiz sinaladi. `Enter` dan OLDINGI tanaffus hisobga olinmaydi —
 * ba'zi skanerlar oxirgi belgidan keyin ichki kechikish bilan yuboradi.
 */
export const classifyKeystrokes = (events: readonly Keystroke[]): ScanVerdict => {
  const endIndex = events.findIndex((event) => event.key === 'Enter');
  if (endIndex < 0) return { kind: 'pending' };

  const typed = events.slice(0, endIndex).filter((event) => isCharacter(event.key));
  const code = typed.map((event) => event.key).join('');
  // Bo'sh `Enter` — odam (skaner hech qachon bo'sh kod yubormaydi).
  if (code === '') return { kind: 'human' };

  const tooSlow = typed.some((event, index) => {
    const previous = typed[index - 1];
    return previous !== undefined && event.at - previous.at >= MAX_SCANNER_GAP_MS;
  });

  return tooSlow ? { kind: 'human' } : { kind: 'scanner', code };
};

/**
 * Ketma-ketlik "qariydi": oxirgi tugmadan keyin shuncha jim turilsa, bufer
 * tozalanadi. Aks holda ertalab yozilgan yarim kod kechqurungi skanerlashga
 * qo'shilib ketardi.
 */
const BUFFER_TTL_MS = 500;

export interface BarcodeScannerState {
  /**
   * Skaner ishlayotgani ANIQLANDI — ekrandagi "Skanerga tayyor" indikatori
   * shundan yashil bo'ladi. Birinchi muvaffaqiyatli o'qishdan keyin yoqiladi.
   */
  detected: boolean;
}

/**
 * Skanerni butun oyna bo'ylab tinglaydi.
 *
 * Nega `input` ning `onKeyDown` i emas: skladchi qo'lqopda ishlaydi va
 * maydonni sichqoncha bilan tanlamaydi — fokus qayerda bo'lishidan qat'i
 * nazar skanerlash ISHLASHI kerak. Fokus maydonda bo'lsa, belgilar unga ham
 * tushadi va bu yaxshi: skladchi kodni ko'rib turadi.
 *
 * Skaner aniqlanganda hodisa TO'XTATILADI (`preventDefault`): aks holda
 * `Enter` shakl yuborilishini ham qo'zg'atib, bitta skanerlash ikki marta
 * ishlab ketardi.
 */
export const useBarcodeScanner = (onScan: (code: string) => void): BarcodeScannerState => {
  const [detected, setDetected] = useState(false);
  const buffer = useRef<Keystroke[]>([]);
  // Chaqiruv ilmoqda `ref` orqali saqlanadi: `onScan` har chizishda yangi
  // funksiya bo'lsa ham, tinglovchi qayta ulanmasligi kerak.
  const handler = useRef(onScan);
  handler.current = onScan;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const now = performance.now();
      const last = buffer.current[buffer.current.length - 1];
      if (last !== undefined && now - last.at > BUFFER_TTL_MS) buffer.current = [];

      buffer.current.push({ key: event.key, at: now });
      const verdict = classifyKeystrokes(buffer.current);
      if (verdict.kind === 'pending') return;

      buffer.current = [];
      if (verdict.kind === 'human') return;

      event.preventDefault();
      setDetected(true);
      handler.current(verdict.code);
    };

    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, []);

  return { detected };
};
